import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import Select from 'react-select';
import "../../Styles/CreateFeeStructure.css";
import { simplePostCall } from '../../api/ApiServices';
import ApiConfig from '../../api/ApiConfig';
import { toast, ToastContainer } from 'react-toastify';
import { Spinner } from 'react-bootstrap';

const CreateFeeStructure = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const classId = location.state?.classId;
  const academicInfo = location.state?.item;
  
  const [rows, setRows] = useState([
    { 
      id: Date.now(), 
      category: '', 
      terms: '', 
      amount: '', 
      demandDate: '', 
      dueDate: '', 
      discounts: [{ id: Date.now() + 1, cat: '', val: '' }], 
      bonuses: [{ id: Date.now() + 2, cat: '', val: '' }] 
    }
  ]);

  const { id } = useParams(); 
  const isEditMode = Boolean(id);

  const [removedRecords, setRemovedRecords] = useState({
    rows: [],
    discounts: [],
    bonuses: []
  });

  const [errors, setErrors] = useState({});
  const [selectedGrade, setSelectedGrade] = useState("");
  // Multi-select selection (create mode only). Stores an array of class_id strings.
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [grades, setGrades] = useState([]);
  const [terms, setTerms] = useState([]);
  const [bonusCategory, setBonusCategory] = useState([]);
  const [discountCategory, setDiscountCategory] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);

  // -------------------------
  // VALIDATION HELPERS
  // -------------------------
  const setFieldError = (key, msg) => {
    setErrors(prev => ({ ...prev, [key]: msg }));
  };

  const clearFieldError = (key) => {
    setErrors(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  // -------------------------
  // INPUT HANDLER
  // -------------------------
  const handleInputChange = (id, field, value) => {
    setRows(prev => {
      const updated = prev.map(row => row.id === id ? { ...row, [field]: value } : row);
      // Validate date relationship inline
      const row = updated.find(r => r.id === id);
      if (row) {
        if (field === 'dueDate' || field === 'demandDate') {
          const demand = field === 'demandDate' ? value : row.demandDate;
          const due = field === 'dueDate' ? value : row.dueDate;
          if (demand && due && due < demand) {
            setFieldError(`${id}-dueDate`, "Due date cannot be earlier than demand date");
          } else {
            clearFieldError(`${id}-dueDate`);
          }
          if (field !== 'dueDate') clearFieldError(`${id}-${field}`);
        } else if (value) {
          clearFieldError(`${id}-${field}`);
        }
      }
      return updated;
    });
  };

  // -------------------------
  // ROW HANDLERS
  // -------------------------
  const addRow = () => {
    setRows([...rows, { 
      id: Date.now(), 
      category: '', 
      terms: '', 
      amount: '', 
      demandDate: '', 
      dueDate: '', 
      discounts: [{ id: Date.now() + 1, cat: '', val: '' }], 
      bonuses: [{ id: Date.now() + 2, cat: '', val: '' }] 
    }]);
  };

  const removeRow = (id) => {
    if (rows.length > 1) {
      const rowToRemove = rows.find(r => r.id === id);
      if (rowToRemove?.element_id) {
        setRemovedRecords(prev => ({
          ...prev,
          rows: [...prev.rows, { ...rowToRemove, status: 'removed' }]
        }));
      }
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const addDiscount = (rowId) => {
    setRows(rows.map(row =>
      row.id === rowId
        ? { ...row, discounts: [...row.discounts, { id: Date.now(), cat: '', val: '' }] }
        : row
    ));
  };

  const removeDiscount = (rowId, discId) => {
    setRows(rows.map(row => {
      if (row.id === rowId && row.discounts.length > 1) {
        const itemToRemove = row.discounts.find(d => d.id === discId);
        if (itemToRemove?.bonus_discount_id) {
          setRemovedRecords(prev => ({
            ...prev,
            discounts: [...prev.discounts, { ...itemToRemove, parent_element_id: row.element_id, status: 'removed' }]
          }));
        }
        return { ...row, discounts: row.discounts.filter(d => d.id !== discId) };
      }
      return row;
    }));
  };

  const addBonus = (rowId) => {
    setRows(rows.map(row =>
      row.id === rowId
        ? { ...row, bonuses: [...row.bonuses, { id: Date.now(), cat: '', val: '' }] }
        : row
    ));
  };

  const removeBonus = (rowId, bonId) => {
    setRows(rows.map(row => {
      if (row.id === rowId && row.bonuses.length > 1) {
        const itemToRemove = row.bonuses.find(b => b.id === bonId);
        if (itemToRemove?.bonus_discount_id) {
          setRemovedRecords(prev => ({
            ...prev,
            bonuses: [...prev.bonuses, { ...itemToRemove, parent_element_id: row.element_id, status: 'removed' }]
          }));
        }
        return { ...row, bonuses: row.bonuses.filter(b => b.id !== bonId) };
      }
      return row;
    }));
  };

  // -------------------------
  // API CALLS
  // -------------------------
  const fetchGrades = async () => {
    try {
      const res = await simplePostCall(ApiConfig.classlist, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
      }));
      if (res?.classread) setGrades(res.classread);
    } catch (err) { console.error(err); }
  };

  const fetchTerms = async () => {
    try {
      const res = await simplePostCall(ApiConfig.termstype, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
      }));
      if (res?.master_academic) {
        const academicYearId = id ? id : academicInfo?.academic_year_id;
        const temp = res?.master_academic?.filter(item => item.terms_academic_year === academicYearId);
        setTerms(temp);
      }
    } catch (err) { console.error(err); }
  };

  const loadDataDounce = async () => {
    try {
      const response = await simplePostCall(ApiConfig.feeCategorylist, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        category_type: "bonus"
      }));
      if (response?.result) setBonusCategory(response.data);
    } catch (error) { console.error(error); }
  };

  const loadDataDiscount = async () => {
    try {
      const response = await simplePostCall(ApiConfig.feeCategorylist, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        category_type: "discount",
      }));
      if (response?.result) setDiscountCategory(response.data);
    } catch (error) { console.error(error); }
  };

  const loadDataStudentFee = async () => {
    try {
      const response = await simplePostCall(ApiConfig.feeCategorylist, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        category_type: "fee",
      }));
      if (response?.result) setCategoryList(response.data);
    } catch (error) { console.error(error); }
  };

  const loadFeeStructureView = async () => {
    try {
      const res = await simplePostCall(ApiConfig.viewFeeStructure, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        grade: classId, 
        academic: id, 
      }));

      if (res?.result) {
        setSelectedGrade(res?.data?.[0]?.grade_id || "");
        if (res.data && Array.isArray(res.data)) {
          const mappedRows = res.data.map((el, index) => {
            const matchedMainCat = categoryList.find(c => c.fee_category_name === el.category_name);
            const mainCategoryId = matchedMainCat ? matchedMainCat.fee_category_id : "";

            const mappedDiscounts = [];
            const mappedBonuses = [];

            if (Array.isArray(el.bonuses_discounts)) {
              el.bonuses_discounts.forEach((bd) => {
                if (bd.type === "discount") {
                  const matchedDisc = discountCategory.find(d => d.fee_category_name === bd.name);
                  mappedDiscounts.push({
                    id: Date.now() + Math.random(),
                    bonus_discount_id: bd.bonus_discount_id,
                    cat: matchedDisc ? matchedDisc.fee_category_id : "",
                    val: bd.amount
                  });
                } else if (bd.type === "bonus") {
                  const matchedBonus = bonusCategory.find(b => b.fee_category_name === bd.name);
                  mappedBonuses.push({
                    id: Date.now() + Math.random(),
                    bonus_discount_id: bd.bonus_discount_id,
                    cat: matchedBonus ? matchedBonus.fee_category_id : "",
                    val: bd.amount
                  });
                }
              });
            }

            return {
              ...el,
              id: Date.now() + index,
              element_id: el.element_id,
              fee_structure_id: el.fee_structure_id,
              category: mainCategoryId,
              terms: el.term_id, 
              amount: el.amount,
              demandDate: el.demand_date ? el.demand_date.split("T")[0] : "",
              dueDate: el.due_date ? el.due_date.split("T")[0] : "",
              discounts: mappedDiscounts.length > 0 ? mappedDiscounts : [{ id: Date.now() + Math.random(), cat: "", val: "" }],
              bonuses: mappedBonuses.length > 0 ? mappedBonuses : [{ id: Date.now() + Math.random(), cat: "", val: "" }]
            };
          });
          if (mappedRows.length > 0) setRows(mappedRows);
        }
      }
    } catch (err) {
      toast.error("Failed to load fee structure");
    }
  };

  // -------------------------
  // EFFECTS
  // -------------------------
  useEffect(() => {
    loadDataDiscount();
    fetchGrades();
    fetchTerms();
    loadDataDounce();
    loadDataStudentFee();
  }, []);

  useEffect(() => {
    if (id && categoryList?.length > 0 && discountCategory?.length > 0 && bonusCategory?.length > 0) {
      loadFeeStructureView();
    }
  }, [id, categoryList, discountCategory, bonusCategory]);

  // -------------------------
  // SUBMISSION LOGIC
  // -------------------------
  const validateForm = () => {
    const newErrors = {};
    const gradeOk = isEditMode ? Boolean(selectedGrade) : selectedGrades.length > 0;
    if (!gradeOk) newErrors.grade = "Please select a grade";
    rows.forEach(row => {
      if (!row.category) newErrors[`${row.id}-category`] = "Required";
      if (!row.terms) newErrors[`${row.id}-terms`] = "Required";
      if (!row.amount) newErrors[`${row.id}-amount`] = "Required";
      if (!row.demandDate) newErrors[`${row.id}-demandDate`] = "Required";
      if (!row.dueDate) {
        newErrors[`${row.id}-dueDate`] = "Required";
      } else if (row.demandDate && row.dueDate < row.demandDate) {
        newErrors[`${row.id}-dueDate`] = "Due date cannot be earlier than demand date";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateFeeStructure = async () => {
    if (!validateForm()) return;
    setCreateLoading(true);
    try {
      const requestBody = {
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        academic: academicInfo?.academic_year_id,
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        grade: selectedGrades,
        category_term_mapping: rows.map((row) => ({
          category_id: Number(row.category),
          amount: Number(row.amount),
          demand_date: row.demandDate,
          due_date: row.dueDate,
          frequency_id: 2,
          term_id: Number(row.terms),
          discount_category: row.discounts.filter(d => d.cat).map(d => ({ category_id: Number(d.cat), category_amount: Number(d.val) || 0 })),
          bonus_category: row.bonuses.filter(b => b.cat).map(b => ({ category_id: Number(b.cat), category_amount: Number(b.val) || 0 }))
        }))
      };
      const response = await simplePostCall(ApiConfig.createFeeStructure, JSON.stringify(requestBody));
      if (response?.result) {
        navigate(`/viewfeestructure2/${response?.model?.[0]?.fee_structure_accademic_year}`, { state: { feeStructureFromCreate: selectedGrades[0], from: "create-fee" } });
      } else {
        toast.error("Failed to create fee structure");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally { setCreateLoading(false); }
  };

  const handleUpdateFeeStructure = async () => {
    if (!validateForm()) return;
    setCreateLoading(true);
    try {
      const requestBody = {
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        academic: id, 
        grade: selectedGrade,
        category_term_mapping: [
          ...rows.map((row) => ({
            ...(row.element_id ? { element_id: row.element_id } : {}),
            ...(row.fee_structure_id ? { fee_structure_id: row.fee_structure_id } : {}),
            category_id: Number(row.category),
            amount: Number(row.amount),
            demand_date: row.demandDate,
            due_date: row.dueDate,
            frequency_id: 2,
            term_id: Number(row.terms),
            discount_category: [
                ...row.discounts.filter(d => d.cat).map(d => ({ ...(d.bonus_discount_id ? { bonus_discount_id: d.bonus_discount_id } : {}), category_id: Number(d.cat), category_amount: Number(d.val) })),
                ...removedRecords.discounts.filter(d => d.parent_element_id === row.element_id).map(d => ({ bonus_discount_id: d.bonus_discount_id, category_id: Number(d.cat), category_amount: Number(d.val), status: "removed" }))
            ],
            bonus_category: [
                ...row.bonuses.filter(b => b.cat).map(b => ({ ...(b.bonus_discount_id ? { bonus_discount_id: b.bonus_discount_id } : {}), category_id: Number(b.cat), category_amount: Number(b.val) })),
                ...removedRecords.bonuses.filter(b => b.parent_element_id === row.element_id).map(b => ({ bonus_discount_id: b.bonus_discount_id, category_id: Number(b.cat), category_amount: Number(b.val), status: "removed" }))
            ]
          })),
          ...removedRecords.rows.map(row => ({
            ...row,
            category_id: Number(row.category),
            amount: Number(row.amount),
            term_id: Number(row.terms),
            status: "removed"
          }))
        ]
      };
      const res = await simplePostCall(ApiConfig.editFeeStructure, JSON.stringify(requestBody));
      if (res?.result) {
        toast.success("Fee structure updated successfully");
        navigate(`/viewfeestructure2/${res?.academic}`, { state: { feeStructureFromCreate: res?.grade, from: "create-fee" } });
      } else { toast.error(res?.message || "Update failed"); }
    } catch (err) { toast.error("Something went wrong"); }
    finally { setCreateLoading(false); }
  };

  return (
    <div className="CFS-page-wrapper">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="CFS-special-header-row">
        <h2 className="CFS-header-title">{isEditMode ? "Update Fee Structure" : "Create Fee Structure"}</h2>
      </div>

      <div className="CFS-main-card">
        <div className="CFS-form-body">
          <div className="CFS-top-inputs">
            <div className="CFS-input-group">
              <label>Academic Year <span className="CFS-star">*</span></label>
              <input type="text" value="2025-2026" disabled className="CFS-main-input disabled" />
            </div>

            <div className="CFS-input-group">
              <label>Grade <span className="CFS-star">*</span></label>
              {isEditMode ? (
                <select
                  className={`CFS-main-input ${errors.grade ? "is-invalid" : ""}`}
                  value={selectedGrade}
                  onChange={(e) => { setSelectedGrade(e.target.value); clearFieldError("grade"); }}
                >
                  <option value="">Select Grade</option>
                  {grades.map((grade) => (
                    <option key={grade.class_id} value={grade.class_id}>{grade.class_name}</option>
                  ))}
                </select>
              ) : (
                <Select
                  isMulti
                  classNamePrefix="cfs-grade-select"
                  placeholder="Select one or more grades"
                  value={grades
                    .filter((g) => selectedGrades.includes(String(g.class_id)))
                    .map((g) => ({ value: String(g.class_id), label: g.class_name }))}
                  options={grades.map((g) => ({ value: String(g.class_id), label: g.class_name }))}
                  onChange={(opts) => {
                    setSelectedGrades((opts || []).map((o) => o.value));
                    clearFieldError("grade");
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: 38,
                      borderColor: errors.grade ? "#dc3545" : base.borderColor,
                      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(83,183,232,0.25)" : "none",
                    }),
                  }}
                />
              )}
              {errors.grade && <div className="invalid-feedback d-block">{errors.grade}</div>}
            </div>
          </div>

          <div className="CFS-mapping-title">Category-Term Mapping</div>

          {/* Table Header */}
          <div className="CFS-table-header">
            <div className="CFS-col">Category</div>
            <div className="CFS-col">Terms</div>
            <div className="CFS-col">Amount</div>
            <div className="CFS-col">Demand Date</div>
            <div className="CFS-col">Due Date</div>
            <div className="CFS-col-action">
              {!id && <button className="CFS-btn-plus-main" onClick={addRow}><Plus size={18} /></button>}
            </div>
          </div>
          
          {rows.map((row) => (
            <div key={row.id} className="CFS-row-container">
              <div className="CFS-main-row">
                <div className="CFS-col">
                  <select
                    className={`CFS-select ${errors[`${row.id}-category`] ? "is-invalid" : ""}`}
                    value={row.category}
                    onChange={(e) => handleInputChange(row.id, "category", e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {categoryList.map((item) => (
                      <option key={item.fee_category_id} value={item.fee_category_id}>{item.fee_category_name}</option>
                    ))}
                  </select>
                </div>

                <div className="CFS-col">
                  <select
                    className={`CFS-select ${errors[`${row.id}-terms`] ? "is-invalid" : ""}`}
                    value={row.terms}
                    onChange={(e) => handleInputChange(row.id, "terms", e.target.value)}
                  >
                    <option value="">Select Term</option>
                    {terms.map((term) => (
                      <option key={term.terms_id} value={term.terms_id}>{term.terms_name}</option>
                    ))}
                  </select>
                </div>

                <div className="CFS-col">
                  <input
                    type="number"
                    placeholder="Amount"
                    value={row.amount}
                    className={`CFS-input ${errors[`${row.id}-amount`] ? "is-invalid" : ""}`}
                    onChange={(e) => handleInputChange(row.id, "amount", e.target.value)}
                  />
                </div>

                {/* Fixed Date Selection Column */}
                <div className="CFS-col">
                  <input
                    type="date"
                    value={row.demandDate}
                    className={`CFS-input ${errors[`${row.id}-demandDate`] ? "is-invalid" : ""}`}
                    onChange={(e) => handleInputChange(row.id, "demandDate", e.target.value)}
                  />
                </div>

                <div className="CFS-col" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <input
                    type="date"
                    value={row.dueDate}
                    className={`CFS-input ${errors[`${row.id}-dueDate`] ? "is-invalid" : ""}`}
                    onChange={(e) => handleInputChange(row.id, "dueDate", e.target.value)}
                    style={{ width: '100%' }}
                  />
                  {errors[`${row.id}-dueDate`] && (
                    <div className="invalid-feedback" style={{ display: 'block', fontSize: '11px' }}>
                      {errors[`${row.id}-dueDate`]}
                    </div>
                  )}
                </div>

                <button className="CFS-btn-minus-main" onClick={() => removeRow(row.id)}>
                  <Minus size={18} />
                </button>
              </div>

              {/* Sub-sections for Discounts and Bonuses */}
              <div className="CFS-sub-section">
                <div className="CFS-sub-column">
                  {row.discounts.map((d) => (
                    <div key={d.id} className="CFS-sub-group">
                      <select
                        className="CFS-sub-select"
                        value={d.cat}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRows(rows.map(r => r.id === row.id ? { ...r, discounts: r.discounts.map(x => x.id === d.id ? { ...x, cat: val } : x) } : r));
                        }}
                      >
                        <option value="">Discount Category</option>
                        {discountCategory.map((item) => (
                          <option key={item.fee_category_id} value={item.fee_category_id}>
                            {item.fee_category_name} ({item.fee_category_amount_type === 'percentage' ? '%' : '$'})
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Amt/%"
                        className="CFS-sub-input"
                        value={d.val}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRows(rows.map(r => r.id === row.id ? { ...r, discounts: r.discounts.map(x => x.id === d.id ? { ...x, val } : x) } : r));
                        }}
                      />
                      <button className="CFS-btn-plus-sub" onClick={() => addDiscount(row.id)}><Plus size={14} /></button>
                      <button className="CFS-btn-minus-sub" onClick={() => removeDiscount(row.id, d.id)}><Minus size={14} /></button>
                    </div>
                  ))}
                </div>

                <div className="CFS-sub-column">
                  {row.bonuses.map((b) => (
                    <div key={b.id} className="CFS-sub-group">
                      <select
                        className="CFS-sub-select"
                        value={b.cat}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRows(rows.map(r => r.id === row.id ? { ...r, bonuses: r.bonuses.map(x => x.id === b.id ? { ...x, cat: val } : x) } : r));
                        }}
                      >
                        <option value="">Bonus Category</option>
                        {bonusCategory.map((item) => (
                          <option key={item.fee_category_id} value={item.fee_category_id}>
                            {item.fee_category_name} ({item.fee_category_amount_type === 'percentage' ? '%' : '$'})
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Amt/%"
                        className="CFS-sub-input"
                        value={b.val}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRows(rows.map(r => r.id === row.id ? { ...r, bonuses: r.bonuses.map(x => x.id === b.id ? { ...x, val } : x) } : r));
                        }}
                      />
                      <button className="CFS-btn-plus-sub" onClick={() => addBonus(row.id)}><Plus size={14} /></button>
                      <button className="CFS-btn-minus-sub" onClick={() => removeBonus(row.id, b.id)}><Minus size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="CFS-footer">
          <button className="CFS-btn-create" disabled={createLoading} onClick={isEditMode ? handleUpdateFeeStructure : handleCreateFeeStructure}>
            {createLoading ? <Spinner size="sm" /> : isEditMode ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFeeStructure;